import * as React from 'react'
import {
  useCancelIngestJobMutation,
  type TabularConfigBuildDetail
} from '@axilon-apps/domains-ontology-ui-state'
import { Loader2, Wrench } from 'lucide-react'
import { Link } from 'react-router-dom'
import { JobLogs } from '../../components/JobLogs/JobLogs'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '../../components/ui/alert-dialog'
import { Button } from '../../components/ui/button'
import { Card } from '../../components/ui/card'
import { SECTION_LABEL } from '../IngestPage/shared/compact'
import { extractErrorMessage } from '../IngestPage/util/extractErrorMessage'
import {
  tabularConfigBuildPath,
  tabularConfigBuildToolCallsPath,
  tabularJobPath
} from '../TabularIngestJobPage/route'
import type { BuildOutcome } from './util/configView'
import { formatWhen, reasonText, verifiesOnly } from './util/formatMeta'

type BuildState = TabularConfigBuildDetail['state']

/** The builder is still running (or waiting to): Stop is meaningful. */
export const isStoppableBuildState = (state: BuildState | undefined) =>
  state === 'uploading' || state === 'queued' || state === 'processing'

/**
 * A build with no draft to show: the live log while the builder works, or
 * what ended it. Tool calls have their own page (debug mode), linked from the
 * spine.
 */
export function BuildPanel({
  build,
  outcome
}: {
  build: TabularConfigBuildDetail
  /** Absent while the builder is still working. */
  outcome?: BuildOutcome
}) {
  const parentJobId = 'parentJobId' in build.reason ? build.reason.parentJobId : undefined
  return (
    <Card className="min-w-0 gap-3 p-4">
      <div className="flex flex-col gap-1">
        <h2 className={SECTION_LABEL}>
          {outcome ? endedTitle(outcome) : `Building draft v${build.proposedVersion}`}
        </h2>
        <p className="text-xs leading-5 text-ink-2" data-testid="build-status">
          {outcome
            ? endedText(build, outcome)
            : `${reasonText(build.reason)} · ${
                verifiesOnly(build)
                  ? 'the example pages are being read again with the new text.'
                  : 'the builder is preparing and verifying a draft.'
              }`}
          {parentJobId ? (
            <>
              {' '}
              Started by{' '}
              <Link className="underline" to={tabularJobPath(parentJobId)}>
                job {parentJobId}
              </Link>
              .
            </>
          ) : null}
        </p>
      </div>
      <div className="max-h-[28rem] min-w-0 overflow-y-auto" data-testid="build-log">
        <JobLogs jobId={build.jobId} emptyHint="Waiting for the config builder to start…" />
      </div>
    </Card>
  )
}

/** The spine's debug-only link to the build's tool-calls page. */
export function ToolCallsLink({ name, build }: { name: string; build: TabularConfigBuildDetail }) {
  return (
    <Button
      asChild
      size="sm"
      variant="ghost"
      className="h-7 px-2 text-xs"
      data-testid="tool-calls-link"
    >
      <Link to={tabularConfigBuildToolCallsPath(name, build.jobId)}>
        <Wrench className="size-3" aria-hidden />
        Tool calls
      </Link>
    </Button>
  )
}

function endedTitle(outcome: BuildOutcome): string {
  switch (outcome) {
    case 'failed':
      return 'Build failed'
    case 'stopped':
      return 'Build stopped'
    case 'rejected':
      return 'Draft rejected'
    case 'superseded':
      return 'Draft revised'
  }
}

function endedText(build: TabularConfigBuildDetail, outcome: BuildOutcome): React.ReactNode {
  switch (outcome) {
    case 'failed':
      return `Build failed: ${build.result.error?.message ?? build.error ?? 'unknown error'}`
    case 'stopped':
      return build.state === 'cancelling'
        ? 'Stopping the config builder…'
        : 'This build was stopped before it proposed a draft. The uploaded example files are kept with the job; start a new build to try again.'
    case 'rejected':
      return `The draft was rejected by ${build.result.rejection?.rejectedBy ?? 'someone'}${
        build.result.rejection?.note ? `: “${build.result.rejection.note}”` : '.'
      }`
    case 'superseded': {
      const { revision } = build.result
      return (
        <>
          Revised by {revision?.revisedBy ?? 'someone'}
          {revision?.revisedAt ? ` at ${formatWhen(revision.revisedAt)}` : ''}
          {revision?.note ? `: “${revision.note}”` : '.'}
          {revision?.supersededBy ? (
            <>
              {' '}
              <Link
                className="underline"
                to={tabularConfigBuildPath(build.configName, revision.supersededBy)}
              >
                Open the new build →
              </Link>
            </>
          ) : null}
        </>
      )
    }
  }
}

/** The spine's Stop button with its confirmation, for a build still running. */
export function StopBuildButton({
  name,
  build
}: {
  name: string
  build: TabularConfigBuildDetail
}) {
  const [cancelIngestJob, cancelState] = useCancelIngestJobMutation()
  const [open, setOpen] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const stopping = build.state === 'cancelling' || cancelState.isLoading
  if (!isStoppableBuildState(build.state) && !stopping) return null
  const confirm = async () => {
    setError(null)
    try {
      await cancelIngestJob(build.jobId).unwrap()
      setOpen(false)
    } catch (err: unknown) {
      // Stay open: the error belongs next to the button that caused it.
      setError(extractErrorMessage(err, 'Stop failed'))
    }
  }
  const openDialog = (next: boolean) => {
    if (next) setError(null)
    setOpen(next)
  }
  return (
    <>
      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={stopping}
        onClick={() => openDialog(true)}
        className="h-7 border-st-down/40 px-2.5 text-xs text-st-down hover:bg-st-down-bg hover:text-st-down"
        data-testid="config-build-stop"
      >
        {stopping ? <Loader2 className="size-3 animate-spin" aria-hidden /> : null}
        {stopping ? 'Stopping…' : 'Stop build'}
      </Button>
      <AlertDialog open={open} onOpenChange={openDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Stop this config build?</AlertDialogTitle>
            <AlertDialogDescription>
              The builder stops at its next tool call and no draft is proposed for {name}. The
              uploaded example files are kept with the job; start a new build to try again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {error ? (
            <p role="alert" className="text-xs text-st-down" data-testid="config-build-stop-error">
              {error}
            </p>
          ) : null}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={cancelState.isLoading}>Keep building</AlertDialogCancel>
            <AlertDialogAction
              variant="outline"
              className="border-st-down bg-st-down text-white shadow-sm hover:bg-st-down/90 hover:text-white dark:border-st-down dark:bg-st-down dark:text-white dark:hover:bg-st-down/90 dark:hover:text-white"
              disabled={cancelState.isLoading}
              onClick={(event) => {
                event.preventDefault()
                void confirm()
              }}
            >
              Stop
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
